import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import PhoneInput from "react-phone-number-input/input";
import { isValidPhoneNumber } from "libphonenumber-js";
import { Form } from "react-bootstrap";
import Popup from "reactjs-popup";
import { User, Mail, MapPin, House, Check, Download, Target } from "lucide-react";
import MarkdownComponent from "../shared-components/MarkDown/MarkdownComponents";
import { getDocumentPath } from "../../constants/brandDocuments";
import { detectBrand } from "../../utils/brandDetection";
import { BRAND_IDS } from "../../config/brands";
import { downloadMarkdownAsPDF } from "../../utils/pdfDownload";
import { trackKlaviyo } from "../../utils/klaviyo";
import "reactjs-popup/dist/index.css";

import Text from "../shared-components/Text";
import EmailInput from "../shared-components/EmailInput";
import FormActionButton from "../shared-components/FormActionButton";
import Select from "../shared-components/Select";
import DatePicker from "../shared-components/DatePicker";
import ButtonComponent from "../shared-components/Button";
import {
	ADDRESS_ENDPOINT,
	ADDRESS_RETRIEVE,
	VERIFY_CLIENT,
	VERIFY_CLIENT_TOKEN,
	formPages,
} from "../../constants";
import { isValidEmail } from "../../utils/utils";
import { submitClientWithKount } from "../../utils/kountSubmission";
import { isValidName } from "../../utils/utils";
import { mapVerifiedClientDetails } from "../../utils/verifiedClientDetails";
import {
	updateFormData,
	setCurrentStep,
	resetFormData,
} from "../../store/actions/formActions";
import "./ConfirmDetails.css";

const titleOptions = [
	{ text: "Mr", value: "MR" },
	{ text: "Mrs", value: "MRS" },
	{ text: "Ms", value: "MS" },
	{ text: "Miss", value: "MISS" },
	{ text: "Other", value: "MX" },
];

const joinAddressParts = (parts) =>
	parts
		.filter(Boolean)
		.map((part) => String(part).trim())
		.filter(Boolean)
		.join(", ");

const formatAddress = (address) => {
	if (!address) return "";
	if (typeof address === "string") return address;

	const loqateAddress = joinAddressParts([
		address.Company,
		address.Line1,
		address.Line2,
		address.Line3,
		address.City,
		address.CountryName,
		address.PostalCode,
	]);
	const recordAddress = joinAddressParts([
		address.address_line_1,
		address.address_line_2,
		address.address_line_3,
		address.address_city,
		address.address_postcode,
	]);

	return address.Text || loqateAddress || recordAddress;
};

const getAddressSearchValue = (address) =>
	address?.Text ||
	address?.PostalCode ||
	address?.address_postcode ||
	formatAddress(address);

const normalizeAddressList = (addresses) => {
	const list = Array.isArray(addresses)
		? addresses
		: addresses && typeof addresses === "object"
			? [addresses]
			: [];

	return list.filter((address) => formatAddress(address));
};

const isBlank = (value) => !String(value ?? "").trim();

const buildPreviousAddressInputs = (addresses) =>
	addresses.map((address) => ({
		postcode: getAddressSearchValue(address),
		addressData: null,
		addressSelected: address,
		errors: { postcode: false, selection: false, duplicate: false },
		isSearching: false,
	}));

export default function ConfirmDetails({ compensate, onNext }) {
	const dispatch = useDispatch();

	const isSmsJourney =
		compensate.marketingChannel?.trim().toLowerCase() === "sms";
	const requiresSurnameVerification =
		compensate.surnameVerificationRequired === true || isSmsJourney;
	const verificationTokenKey = compensate.uniqueID
		? `sms_verification_token_${compensate.uniqueID}`
		: "";
	const [smsDetailsVerified, setSmsDetailsVerified] = useState(false);
	const showStoredDetails =
		!requiresSurnameVerification || smsDetailsVerified;

	const initialPreviousAddresses = showStoredDetails
		? normalizeAddressList(compensate.previousAddress)
		: [];

	const [title, setTitle] = useState(
		showStoredDetails ? (compensate.title ?? "") : (compensate.title ?? ""),
	);
	const [firstName, setFirstName] = useState(compensate.firstName ?? "");
	const [middleName, setMiddleName] = useState(
		showStoredDetails ? (compensate.middleName ?? "") : "",
	);
	const [surname, setSurname] = useState(
		requiresSurnameVerification ? "" : (compensate.surname ?? ""),
	);
	const [previousName, setPreviousName] = useState(
		showStoredDetails ? (compensate.previousName ?? "") : "",
	);
	const [dob, setDob] = useState(
		showStoredDetails ? (compensate.dob ?? "") : "",
	);
	const [detailsError, setDetailsError] = useState(false);
	const [email, setEmail] = useState(
		showStoredDetails ? (compensate.email ?? "") : "",
	);
	const [mobile, setMobile] = useState(
		showStoredDetails ? (compensate.mobile ?? "") : "",
	);
	const [emailError, setEmailError] = useState(false);
	const [previousAddresses, setPreviousAddresses] = useState(
		initialPreviousAddresses,
	);
	const [previousAddressInputs, setPreviousAddressInputs] = useState(() =>
		buildPreviousAddressInputs(initialPreviousAddresses),
	);

	const [postcode, setPostcode] = useState(
		showStoredDetails ? (getAddressSearchValue(compensate.currentAddress) ?? "") : "",
	);
	const [suggestions, setSuggestions] = useState([]);
	const [selectedAddress, setSelectedAddress] = useState(
		showStoredDetails ? (compensate.currentAddress ?? null) : null,
	);
	const [isSearching, setIsSearching] = useState(false);
	const [addressSelectionError, setAddressSelectionError] = useState(false);

	const [mobileError, setMobileError] = useState(false);
	const submittingRef = useRef(false);
	const [submitting, setSubmitting] = useState(false);
	const [signatureError, setSignatureError] = useState(false);
	const hideReadyCopy =
		compensate.marketingChannel?.trim().toLowerCase() === "email";

	const handleRestartClaim = () => {
		if (verificationTokenKey) {
			sessionStorage.removeItem(verificationTokenKey);
		}
		if (compensate.uniqueID) {
			sessionStorage.removeItem(`sms_verified_${compensate.uniqueID}`);
		}
		window.history.replaceState({}, "", window.location.pathname);
		dispatch(resetFormData());
		dispatch(setCurrentStep(formPages.NewClaimantDetails));
	};

	const surnameVerifyingRef = useRef(false);
	const [surnameVerifying, setSurnameVerifying] = useState(false);
	const [surnameVerifyError, setSurnameVerifyError] = useState("");
	const [surnameAttempts, setSurnameAttempts] = useState(0);
	const surnameLockedOut = surnameAttempts >= 3;

	const applyVerifiedDetails = useCallback(
		(verified) => {
			const mapped = mapVerifiedClientDetails(verified);
			setTitle(mapped.title);
			setSurname(mapped.surname);
			setMiddleName(mapped.middleName);
			setPreviousName(mapped.previousName);
			setDob(mapped.dob);
			setEmail(mapped.email);
			setMobile(mapped.mobile);
			setSelectedAddress(mapped.currentAddress);
			setPostcode(mapped.currentPostcode);

			const verifiedPreviousAddresses = normalizeAddressList(
				mapped.previousAddress,
			);
			setPreviousAddresses(verifiedPreviousAddresses);
			setPreviousAddressInputs(
				buildPreviousAddressInputs(verifiedPreviousAddresses),
			);

			dispatch(updateFormData(mapped));
			setSmsDetailsVerified(true);
			setSurnameVerifyError("");
			setSurnameAttempts(0);
			setDetailsError(false);

			trackKlaviyo("Verified", {
				url: window.location.href,
				page_name: "ConfirmDetails",
				email: mapped.email,
				is_sms_verify_flow: true,
			});
		},
		[dispatch],
	);

	useEffect(() => {
		if (!requiresSurnameVerification || !compensate.uniqueID || !verificationTokenKey) return;

		const verificationToken = sessionStorage.getItem(verificationTokenKey);
		if (!verificationToken) return;

		let cancelled = false;
		const restoreVerification = async () => {
			try {
				const verified = await VERIFY_CLIENT_TOKEN(
					compensate.uniqueID,
					verificationToken,
				);
				if (cancelled) return;
				if (!verified?.uid) {
					sessionStorage.removeItem(verificationTokenKey);
					return;
				}
				applyVerifiedDetails(verified);
			} catch (_error) {
				if (!cancelled) {
					sessionStorage.removeItem(verificationTokenKey);
				}
			}
		};

		restoreVerification();
		return () => {
			cancelled = true;
		};
	}, [
		applyVerifiedDetails,
		compensate.uniqueID,
		requiresSurnameVerification,
		verificationTokenKey,
	]);

	const registerSurnameMismatch = () => {
		const next = surnameAttempts + 1;
		setSurnameAttempts(next);
		setSurnameVerifyError(
			next >= 3
				? "We failed to match your surname, please start your claim here or using the button below."
				: `The surname doesn't match our records. Please try again. (${3 - next} attempt${3 - next > 1 ? "s" : ""} remaining.)`,
		);
	};

	const handleSurnameVerify = async () => {
		if (
			!requiresSurnameVerification ||
			smsDetailsVerified ||
			surnameLockedOut ||
			surnameVerifyingRef.current
		) {
			return;
		}

		setSurnameVerifyError("");
		const trimmed = surname.trim();
		if (!trimmed || !isValidName(trimmed)) {
			registerSurnameMismatch();
			return;
		}

		surnameVerifyingRef.current = true;
		setSurnameVerifying(true);
		try {
			const verified = await VERIFY_CLIENT(
				compensate.uniqueID,
				compensate.firstName,
				trimmed,
			);
			if (!verified?.uid) {
				registerSurnameMismatch();
				return;
			}

			const verificationToken = verified.verification_token;
			if (verificationTokenKey && verificationToken) {
				sessionStorage.setItem(verificationTokenKey, verificationToken);
			}
			applyVerifiedDetails(verified);
		} catch (_error) {
			setSurnameVerifyError(
				"We couldn't verify your details because of a connection problem. Please try again; this has not used an attempt.",
			);
		} finally {
			surnameVerifyingRef.current = false;
			setSurnameVerifying(false);
		}
	};

	const handleSurnameKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleSurnameVerify();
		}
	};

	const debouncedSearch = useRef(
		debounce(async (query) => {
			if (!query || query.trim().length < 3) {
				setSuggestions([]);
				setIsSearching(false);
				return;
			}
			setIsSearching(true);
			try {
				const data = await ADDRESS_ENDPOINT(compensate, query);
				setSuggestions(data || []);
			} catch (e) {
				console.error("Address search error:", e);
				setSuggestions([]);
			}
			setIsSearching(false);
		}, 100),
	).current;
	const previousSearchRefs = useRef({});

	const handleSuggestionClick = async (item) => {
		if (item.Type === "Address") {
			const data = await ADDRESS_RETRIEVE(item.Id);
			setSelectedAddress(data);
			setPostcode(item.Text);
			setSuggestions([]);
			setAddressSelectionError(false);
		} else {
			setIsSearching(true);
			const data = await ADDRESS_ENDPOINT(compensate, item.Text, item.Id);
			setSuggestions(data || []);
			setPostcode(item.Text);
			setIsSearching(false);
		}
	};

	const getPreviousAddressLabel = (index) =>
		index === 0 ? "Previous address" : "Additional previous address";

	const addPreviousAddress = () => {
		if (previousAddressInputs.length >= 2) return;

		setPreviousAddressInputs((prev) => [
			...prev,
			{
				postcode: "",
				addressData: null,
				addressSelected: null,
				errors: { postcode: false, selection: false, duplicate: false },
				isSearching: false,
			},
		]);
	};


	const updatePreviousAddressInput = (index, postcodeValue) => {
		setPreviousAddressInputs((prev) =>
			prev.map((item, itemIndex) =>
				itemIndex === index
					? {
						...item,
						postcode: postcodeValue,
						addressSelected: null,
						errors: { postcode: false, selection: false, duplicate: false },
					}
					: item,
			),
		);
		setPreviousAddresses((prev) =>
			prev.map((address, addressIndex) =>
				addressIndex === index ? null : address,
			),
		);
	};

	const clearPreviousAddressErrors = (index) => {
		setPreviousAddressInputs((prev) =>
			prev.map((item, itemIndex) =>
				itemIndex === index
					? {
						...item,
						errors: { postcode: false, selection: false, duplicate: false },
					}
					: item,
			),
		);
	};

	const getDebouncedPreviousSearch = (index) => {
		if (!previousSearchRefs.current[index]) {
			previousSearchRefs.current[index] = debounce(async (query) => {
				if (!query || query.trim().length < 3) {
					setPreviousAddressInputs((prev) =>
						prev.map((item, itemIndex) =>
							itemIndex === index
								? { ...item, addressData: null, isSearching: false }
								: item,
						),
					);
					return;
				}

				setPreviousAddressInputs((prev) =>
					prev.map((item, itemIndex) =>
						itemIndex === index ? { ...item, isSearching: true } : item,
					),
				);

				try {
					const data = await ADDRESS_ENDPOINT(compensate, query);
					setPreviousAddressInputs((prev) =>
						prev.map((item, itemIndex) =>
							itemIndex === index
								? { ...item, addressData: data || [], isSearching: false }
								: item,
						),
					);
				} catch (error) {
					console.error("Previous address search error:", error);
					setPreviousAddressInputs((prev) =>
						prev.map((item, itemIndex) =>
							itemIndex === index
								? { ...item, addressData: null, isSearching: false }
								: item,
						),
					);
				}
			}, 100);
		}

		return previousSearchRefs.current[index];
	};

	const handlePreviousSuggestionClick = async (item, index) => {
		if (item.Type === "Address") {
			const data = await ADDRESS_RETRIEVE(item.Id);
			setPreviousAddresses((prev) => {
				const next = [...prev];
				next[index] = data;
				return next;
			});
			setPreviousAddressInputs((prev) =>
				prev.map((input, inputIndex) =>
					inputIndex === index
						? {
							...input,
							addressSelected: item,
							postcode: item.Text,
							addressData: null,
							errors: { postcode: false, selection: false, duplicate: false },
							isSearching: false,
						}
						: input,
				),
			);
			return;
		}

		setPreviousAddressInputs((prev) =>
			prev.map((input, inputIndex) =>
				inputIndex === index ? { ...input, isSearching: true } : input,
			),
		);
		const data = await ADDRESS_ENDPOINT(compensate, item.Text, item.Id);
		setPreviousAddressInputs((prev) =>
			prev.map((input, inputIndex) =>
				inputIndex === index
					? {
						...input,
						addressData: data || [],
						postcode: item.Text,
						isSearching: false,
					}
					: input,
			),
		);
	};

	useEffect(() => {
		const previousSearches = previousSearchRefs.current;

		return () => {
			debouncedSearch.cancel();
			Object.values(previousSearches).forEach((search) => search.cancel?.());
		};
	}, [debouncedSearch]);

	const handleConfirm = async () => {
		if (submittingRef.current) return;

		if (requiresSurnameVerification && !smsDetailsVerified) return;
		if (
			isBlank(title) ||
			isBlank(firstName) ||
			isBlank(surname) ||
			isBlank(dob)
		) {
			setDetailsError(true);
			return;
		}
		if (isBlank(email) || !isValidEmail(email)) {
			setEmailError(true);
			return;
		}
		if (mobile && !isValidPhoneNumber(mobile, "GB")) {
			setMobileError(true);
			return;
		}
		if (!selectedAddress) {
			setAddressSelectionError(true);
			return;
		}
		let previousAddressHasErrors = false;
		previousAddressInputs.forEach((addressInput, index) => {
			if (!addressInput.postcode) {
				setPreviousAddressInputs((prev) =>
					prev.map((item, itemIndex) =>
						itemIndex === index
							? {
								...item,
								errors: { ...item.errors, postcode: true },
							}
							: item,
					),
				);
				previousAddressHasErrors = true;
				return;
			}

			if (!addressInput.addressSelected || !previousAddresses[index]) {
				setPreviousAddressInputs((prev) =>
					prev.map((item, itemIndex) =>
						itemIndex === index
							? {
								...item,
								errors: { ...item.errors, selection: true },
							}
							: item,
					),
				);
				previousAddressHasErrors = true;
			}
		});

		const addressKeys = [
			selectedAddress,
			...normalizeAddressList(previousAddresses),
		]
			.map((address) => address?.Id || formatAddress(address))
			.filter(Boolean);
		const duplicateKeys = addressKeys.filter(
			(key, index) => addressKeys.indexOf(key) !== index,
		);

		if (duplicateKeys.length) {
			previousAddressInputs.forEach((_, index) => {
				const key =
					previousAddresses[index]?.Id ||
					formatAddress(previousAddresses[index]);
				if (duplicateKeys.includes(key)) {
					setPreviousAddressInputs((prev) =>
						prev.map((item, itemIndex) =>
							itemIndex === index
								? {
									...item,
									errors: { ...item.errors, duplicate: true },
								}
								: item,
						),
					);
					previousAddressHasErrors = true;
				}
			});
		}

		if (previousAddressHasErrors) {
			return;
		}

		const confirmedPreviousAddresses = normalizeAddressList(previousAddresses);
		const updatedData = {
			...compensate,
			title,
			firstName,
			middleName,
			surname,
			previousName,
			dob,
			email,
			confirmEmail: email,
			mobile,
			currentAddress: selectedAddress,
			previousAddress: confirmedPreviousAddresses,
			signature: compensate.signature,
		};
		const signatureValue = updatedData.signature;
		const signatureToken = updatedData.signatureToken;

		// Always require signature — never submit without one
		if (!signatureValue && !signatureToken) {
			// Save confirmed details and flag so Signature page submits directly
			submittingRef.current = true;
			dispatch(updateFormData({ ...updatedData, detailsConfirmed: true }));
			dispatch(setCurrentStep(formPages.Signature));
			return;
		}

		setSignatureError(false);
		dispatch(updateFormData(updatedData));

		submittingRef.current = true;
		setSubmitting(true);
		try {
			const { response, payload } = await submitClientWithKount(updatedData, {
				pageName: "ConfirmDetails",
				signatureValue,
				emailRoute: "kyc_confirm_details",
			});

			onNext(
				{
					...payload,
					uniqueID:
						response?.uniqueID || payload.uniqueID || compensate.uniqueID,
					detailsConfirmed: true,
				},
				formPages.RequestFinanceLender,
			);
		} catch (error) {
			console.error("Error submitting confirmed details:", error);
			if (
				error?.response?.status === 422 &&
				error?.response?.data?.detail === "Invalid signature token"
			) {
				const { signatureToken: _expiredToken, ...dataWithoutToken } = updatedData;
				dispatch(
					updateFormData({ ...dataWithoutToken, detailsConfirmed: true }),
				);
				dispatch(setCurrentStep(formPages.Signature));
			} else {
				setSignatureError(true);
			}
		} finally {
			submittingRef.current = false;
			setSubmitting(false);
		}
	};

	// Brand detection for document links
	const brand = detectBrand();

	const termsAndConditions = (text) => {
		return (
			<Popup
				trigger={
					<button className="sign-up-terms" data-testid="confirm-link-terms">
						{text}
					</button>
				}
				modal
			>
				{(close) => (
					<div className="doc-modal">
						<div className="doc-modal-header">
							<button className="doc-modal-close" onClick={close}>
								×
							</button>
						</div>
						<div className="doc-modal-body">
							<MarkdownComponent
								filePath={getDocumentPath("termsConditions")}
							/>
						</div>
						<div className="doc-modal-footer">
							<button
								className="doc-download-btn"
								onClick={() =>
									downloadMarkdownAsPDF(
										getDocumentPath("termsConditions"),
										"Terms-and-Conditions.pdf",
									)
								}
							>
								<Download aria-hidden="true" size={14} />
								Download PDF
							</button>
						</div>
					</div>
				)}
			</Popup>
		);
	};

	const privacyPolicy = () => {
		return (
			<Popup
				trigger={
					<button
						className="sign-up-terms"
						data-testid="confirm-link-privacy-policy"
					>
						Privacy Policy
					</button>
				}
				modal
			>
				{(close) => (
					<div className="doc-modal">
						<div className="doc-modal-header">
							<button className="doc-modal-close" onClick={close}>
								×
							</button>
						</div>
						<div className="doc-modal-body">
							<MarkdownComponent filePath={getDocumentPath("privacyPolicy")} />
						</div>
						<div className="doc-modal-footer">
							<button
								className="doc-download-btn"
								onClick={() =>
									downloadMarkdownAsPDF(
										getDocumentPath("privacyPolicy"),
										"Privacy-Policy.pdf",
									)
								}
							>
								<Download aria-hidden="true" size={14} />
								Download PDF
							</button>
						</div>
					</div>
				)}
			</Popup>
		);
	};

	const mlmTerms = () => {
		return (
			<Popup
				trigger={
					<button
						className="sign-up-terms"
						data-testid="confirm-link-mlm-terms"
					>
						MLM Terms and Agreement Pack
					</button>
				}
				modal
			>
				{(close) => (
					<div className="doc-modal">
						<div className="doc-modal-header">
							<button className="doc-modal-close" onClick={close}>
								×
							</button>
						</div>
						<div className="doc-modal-body">
							<MarkdownComponent filePath={getDocumentPath("dbaTerms")} />
						</div>
						<div className="doc-modal-footer">
							<button
								className="doc-download-btn"
								onClick={() =>
									downloadMarkdownAsPDF(
										getDocumentPath("dbaTerms"),
										"MLM-Terms-and-DBA.pdf",
									)
								}
							>
								<Download aria-hidden="true" size={14} />
								Download PDF
							</button>
						</div>
					</div>
				)}
			</Popup>
		);
	};

	const valid8PrivacyPolicy = () => {
		return (
			<a
				href="https://www.valid8.co.uk/privacy"
				target="_blank"
				rel="noopener noreferrer"
				className="sign-up-terms"
				data-testid="confirm-link-valid8-privacy-policy"
			>
				Valid8 Privacy Policy
			</a>
		);
	};

	const mlmPrivacyPolicy = () => {
		return (
			<Popup
				trigger={
					<button
						className="sign-up-terms"
						data-testid="confirm-link-mlm-privacy-policy"
					>
						MLM Privacy Policy
					</button>
				}
				modal
			>
				{(close) => (
					<div className="doc-modal">
						<div className="doc-modal-header">
							<button className="doc-modal-close" onClick={close}>
								×
							</button>
						</div>
						<div className="doc-modal-body">
							<MarkdownComponent
								filePath={getDocumentPath("mlmPrivacyPolicy")}
							/>
						</div>
						<div className="doc-modal-footer">
							<button
								className="doc-download-btn"
								onClick={() =>
									downloadMarkdownAsPDF(
										getDocumentPath("mlmPrivacyPolicy"),
										"MLM-Privacy-Policy.pdf",
									)
								}
							>
								<Download aria-hidden="true" size={14} />
								Download PDF
							</button>
						</div>
					</div>
				)}
			</Popup>
		);
	};

	const keyFacts = (testId = "confirm-link-key-facts") => {
		return (
			<Popup
				trigger={
					<button className="sign-up-terms" data-testid={testId}>
						Key Facts
					</button>
				}
				modal
			>
				{(close) => (
					<div className="doc-modal">
						<div className="doc-modal-header">
							<button className="doc-modal-close" onClick={close}>
								×
							</button>
						</div>
						<div className="doc-modal-body">
							<MarkdownComponent filePath={getDocumentPath("keyFacts")} />
						</div>
						<div className="doc-modal-footer">
							<button
								className="doc-download-btn"
								onClick={() =>
									downloadMarkdownAsPDF(
										getDocumentPath("keyFacts"),
										"MLM-Key-Facts.pdf",
										true,
									)
								}
							>
								<Download aria-hidden="true" size={14} />
								Download PDF
							</button>
						</div>
					</div>
				)}
			</Popup>
		);
	};

	const representationConsentStatement = () => {
		return (
			<Popup
				trigger={
					<button
						className="sign-up-terms"
						data-testid="confirm-link-representation-statement"
					>
						Representation Statement
					</button>
				}
				modal
			>
				{(close) => (
					<div className="doc-modal">
						<div className="doc-modal-header">
							<button className="doc-modal-close" onClick={close}>
								×
							</button>
						</div>
						<div className="doc-modal-body">
							<MarkdownComponent
								filePath={getDocumentPath("representationConsentStatement")}
							/>
						</div>
						<div className="doc-modal-footer">
							<button
								className="doc-download-btn"
								onClick={() =>
									downloadMarkdownAsPDF(
										getDocumentPath("representationConsentStatement"),
										"Representation-Statement.pdf",
									)
								}
							>
								<Download aria-hidden="true" size={14} />
								Download PDF
							</button>
						</div>
					</div>
				)}
			</Popup>
		);
	};

	const mlmFees = () => {
		return (
			<Popup
				trigger={
					<button className="sign-up-terms" data-testid="confirm-link-fees">
						Fees
					</button>
				}
				modal
			>
				{(close) => (
					<div className="doc-modal">
						<div className="doc-modal-header">
							<button className="doc-modal-close" onClick={close}>
								×
							</button>
						</div>
						<div className="doc-modal-body">
							<MarkdownComponent filePath={getDocumentPath("mlmFees")} />
						</div>
						<div className="doc-modal-footer">
							<button
								className="doc-download-btn"
								onClick={() =>
									downloadMarkdownAsPDF(
										getDocumentPath("mlmFees"),
										"MLM-Fees.pdf",
									)
								}
							>
								<Download aria-hidden="true" size={14} />
								Download PDF
							</button>
						</div>
					</div>
				)}
			</Popup>
		);
	};

	return (
		<div className="confirm-details-page" data-testid="page-confirm-details">
			<div className="d-flex align-items-center heading-icon row fdc-form">
				{!hideReadyCopy && (!requiresSurnameVerification || !smsDetailsVerified) && (
					<h1 className="text-center" style={{ fontWeight: 700, color: "var(--current-color)", fontSize: "28px", margin: 0, paddingTop: "24px", paddingBottom: "32px" }}>
						Be part of a payout worth up to <span className="ycm-verification-amount">£10 billion!</span> You could be owed <span className="ycm-verification-amount">£829</span> per car finance claim, check now for <span className="ycm-verification-amount">FREE!*</span>
					</h1>
				)}
				{showStoredDetails && !hideReadyCopy && (
					<p style={{ fontSize: "20px", fontWeight: 700, marginTop: "32px", marginBottom: "16px" }}>
						You could be owed <span className="ycm-verification-amount">£829</span> per car finance claim, check now for <span className="ycm-verification-amount">FREE!*</span> The FCA have reported that <span className="ycm-verification-amount">12.1 million</span> car finance agreements may have been affected.
					</p>
				)}
				<h2 className={hideReadyCopy ? "title mb-4" : "title"} style={{ fontSize: "24px", marginTop: "24px", textAlign: !hideReadyCopy && showStoredDetails ? "left" : "center" }}>
					{!hideReadyCopy && showStoredDetails ? (
						<>Hi, <span className="ycm-orange">{compensate.firstName}</span>.</>
					) : (
						<>
							Welcome back, <span className="ycm-orange">{compensate.firstName}</span>
							{!hideReadyCopy && (!requiresSurnameVerification || !smsDetailsVerified) && <>.</>}
						</>
					)}
				</h2>
				{!hideReadyCopy && (!requiresSurnameVerification || !smsDetailsVerified) && (
					<p style={{ fontWeight: 600, textAlign: "center", fontSize: "18px" }}>
						Did you purchase a motor vehicle on finance after 2007?<br />
						You can check if you have a Car Finance compensation claim.
					</p>
				)}
				{!hideReadyCopy && (!requiresSurnameVerification || !smsDetailsVerified) && (
					<p className="mb-0" style={{ fontWeight: 700, fontSize: "18px", textAlign: "center" }}>
						Please enter your surname below to review your information.
					</p>
				)}
				{showStoredDetails && hideReadyCopy && (
					<p style={{ fontSize: "20px", fontWeight: 700, marginBottom: "16px" }}>
						Be part of a payout worth up to <span className="ycm-verification-amount">£10 billion!</span> check now for <span className="ycm-verification-amount">FREE!*</span>
					</p>
				)}
				{showStoredDetails && !hideReadyCopy && (
					<p className="mb-0" style={{ fontWeight: 600 }}>
						As an existing customer, and the information you have previously provided us, you may have a car finance agreement that could be eligible for compensation.
					</p>
				)}
			</div>

			<div className="fdc-form gif-input">
				{!hideReadyCopy && showStoredDetails && (
					<div className={`form-block mb-3 info-box${brand.id === BRAND_IDS.YCM ? " ycm-bg" : ""}`}>
						<div className="d-flex align-items-center gap-2">
							<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--success-color)", flexShrink: 0 }}>
								<path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
								<line x1="12" x2="12" y1="8" y2="12" />
								<line x1="12" x2="12.01" y1="16" y2="16" />
							</svg>
							<span style={{ color: "var(--success-color)", fontWeight: 800, fontSize: "20px" }}>START YOUR CLAIM</span>
						</div>
						<label className="form-label">
							Please check the information below and <span style={{ color: "#000" }}>click on the field name</span> to update anything that's changed and then submit.
						</label>
					</div>
				)}
				<section
					className="confirm-details-section"
					data-testid="confirm-section-details"
				>
					<div className="confirm-details-grid">
						<section
							className="confirm-details-card full-width"
							data-testid="confirm-card-personal"
						>
							<div className="confirm-details-card-header">
								<User aria-hidden="true" size={20} />
								<h3>PERSONAL DETAILS</h3>
							</div>
							{detailsError && (
								<div
									className="error-message"
									data-testid="confirm-error-personal-details"
								>
									<p>
										Please confirm your title, first name, surname and date of birth.
									</p>
								</div>
							)}
							<div className="row">
								<div className="col-12 col-md-4">
									<div className="form-block">
										<Select
											label="Title"
											options={titleOptions}
											value={title}
											onChange={(value) => {
												setTitle(value);
												setDetailsError(false);
											}}
											id="Confirm Title"
											placeholder="Select"
											testId="confirm-title"
										/>
									</div>
								</div>
								<div className="col-6 col-md-4">
									<div className="form-block">
										<Text
											label="First name"
											value={firstName}
											onChange={(e) => {
												setFirstName(e.target.value);
												setDetailsError(false);
											}}
											id="Confirm First Name"
											placeholder="Enter"
											testId="confirm-first-name"
										/>
									</div>
								</div>
								<div className="col-6 col-md-4">
									<div className="form-block">
										<Text
											label="Surname"
											value={surname}
											onChange={(e) => {
												setSurname(e.target.value);
												setDetailsError(false);
												setSurnameVerifyError("");
											}}
											onKeyDown={handleSurnameKeyDown}
											id="Confirm Surname"
											placeholder="Enter"
											testId="confirm-surname"
											disabled={surnameLockedOut}
										/>


									</div>

								</div>
								{showStoredDetails && (
									<div className="col-12 col-md-6">
										<div className="form-block">
											<Text
												label="Previous surname"
												value={previousName}
												onChange={(e) => setPreviousName(e.target.value)}
												id="Confirm Previous Surname"
												placeholder="Enter"
												testId="confirm-previous-surname"
											/>
										</div>
									</div>
								)}
								{showStoredDetails && (
									<div className="col-12 col-md-6">
										<div className="form-block">
											<DatePicker
												label="Date of birth"
												onChange={(formattedDate) => {
													setDob(formattedDate);
													setDetailsError(false);
												}}
												compensate={{ dob }}
												testId="confirm-dob"
											/>
										</div>
									</div>
								)}

							</div>

							{surnameVerifyError && (
								<div className="error-message">
									{surnameLockedOut ? (
										<p>
											We failed to match your surname, please start your claim{" "}
											<button
												type="button"
												className="verification-restart-link"
												data-testid="confirm-btn-start-new-claim-inline"
												onClick={handleRestartClaim}
											>
												here
											</button>{" "}
											or using the button below.
										</p>
									) : (
										<p>{surnameVerifyError}</p>
									)}
								</div>
							)}
						</section>

						{requiresSurnameVerification && surnameLockedOut && (
							<div style={{ display: "flex", justifyContent: "center", gridColumn: "1 / -1", width: "100%", marginTop: "12px" }}>
								<ButtonComponent
									className="btn-w next-button"
									testId="confirm-btn-start-new-claim"
									label="Start a new claim"
									onClick={handleRestartClaim}
									style={{ padding: "6px 16px", fontSize: "0.85rem" }}
								/>
							</div>
						)}

						{requiresSurnameVerification && !smsDetailsVerified && !surnameLockedOut && (
							<div style={{ display: "flex", justifyContent: "center", gridColumn: "1 / -1", width: "100%" }}>
								<ButtonComponent
									className="btn-w next-button ycm-verification-confirm"
									testId="confirm-btn-verify"
									label="Confirm"
									onClick={handleSurnameVerify}
									disabled={surnameVerifying || !surname.trim()}
									style={{ padding: "6px 16px", fontSize: "0.85rem" }}
								/>
							</div>
						)}


						{showStoredDetails && (
							<section
								className="confirm-details-card full-width"
								data-testid="confirm-card-contact"
							>
								<div className="confirm-details-card-header">
									<Mail aria-hidden="true" size={20} />
									<h3>CONTACT DETAILS</h3>
								</div>
								<div className="row">
									<div className="col-12 col-md-6">
										<div className="form-block">
											<EmailInput
												label="Email address"
												placeholder="example@gmail.com"
												value={email}
												onChange={(e) => {
													setEmail(e.target.value);
													if (isValidEmail(e.target.value)) setEmailError(false);
												}}
												id="Confirm Email"
												testId="confirm-email"
											/>
											{emailError && (
												<div
													className="error-message"
													data-testid="confirm-error-email"
												>
													<p>
														Please enter a valid email address
													</p>
												</div>
											)}
										</div>
									</div>
									<div className="col-12 col-md-6">
										<div className="form-block">
											<Form.Label htmlFor="Confirm Mobile" style={{ fontWeight: 500 }}>
												Mobile number (if available)
											</Form.Label>
											<PhoneInput
												id="Confirm Mobile"
												data-testid="confirm-mobile"
												className="phone-input"
												defaultCountry="GB"
												value={mobile || undefined}
												onChange={(value) => {
													setMobile(value || "");
													if (mobileError) setMobileError(false);
												}}
											/>
											{mobileError && (
												<div
													className="error-message"
													data-testid="confirm-error-mobile"
												>
													<p>Please enter a valid UK mobile number</p>
												</div>
											)}
										</div>
									</div>
								</div>

							</section>
						)}

						{showStoredDetails && (
							<>
								<section
									className={`confirm-details-card${!selectedAddress && previousAddressInputs.length === 0 ? " full-width" : ""}`}
									data-testid="confirm-card-current-address"
								>
									<div className="confirm-details-card-header">
										<MapPin aria-hidden="true" size={20} />
										<h3>CURRENT ADDRESS</h3>
									</div>
									<div className="form-block address-autocomplete">
										<Text
											label="Current address"
											htmlFor="Confirm Address Search"
											onChange={(e) => {
												setPostcode(e.target.value);
												setSelectedAddress(null);
												setAddressSelectionError(false);
												debouncedSearch(e.target.value);
											}}
											id="Confirm Address Search"
											value={postcode}
											placeholder="Start typing your address or postcode"
											testId="confirm-address-postcode"
										/>
										{isSearching && <div className="is-searching">Searching…</div>}
										{suggestions.length > 0 && (
											<div className="address-suggestions">
												{suggestions.map((item, idx) => (
													<div
														key={item.Id || idx}
														className="suggestion-item"
														data-testid={`confirm-address-suggestion-${idx}`}
														onClick={() => handleSuggestionClick(item)}
													>
														<div className="suggestion-text">{item.Text}</div>
														{item.Description && (
															<div className="suggestion-description">
																{item.Description}
															</div>
														)}
													</div>
												))}
											</div>
										)}
										{addressSelectionError && (
											<div
												className="error-message"
												data-testid="confirm-error-current-address"
											>
												<p>Please select an address</p>
											</div>
										)}
									</div>
								</section>

								{(selectedAddress || previousAddressInputs.length > 0) && (
									<section
										className="confirm-details-card"
										data-testid="confirm-card-previous-address"
									>
										<div className="confirm-details-card-header">
											<House aria-hidden="true" size={20} />
											<h3>PREVIOUS ADDRESS</h3>
										</div>
										{previousAddressInputs.map((addressInput, index) => (
											<div key={`confirm-previous-address-${index}`}>
												<div className="mb-2 d-flex justify-content-between align-items-center">
													<label style={{ fontWeight: 600 }}>
														{getPreviousAddressLabel(index)}
													</label>
												</div>
												<div className="form-block address-autocomplete">
													<Text
														label=""
														htmlFor={`Confirm Previous Address Search ${index}`}
														onChange={(e) => {
															updatePreviousAddressInput(index, e.target.value);
															clearPreviousAddressErrors(index);
															getDebouncedPreviousSearch(index)(e.target.value);
														}}
														id={`Confirm Previous Address Search ${index}`}
														value={addressInput.postcode}
														placeholder="Start typing your address or postcode"
														testId={`confirm-previous-address-postcode-${index}`}
													/>
													{addressInput.isSearching && (
														<div className="is-searching">Searching…</div>
													)}
													{addressInput.addressData?.length > 0 && (
														<div className="address-suggestions">
															{addressInput.addressData.map((item, itemIndex) => (
																<div
																	key={item.Id || itemIndex}
																	className="suggestion-item"
																	data-testid={`confirm-previous-address-suggestion-${index}-${itemIndex}`}
																	onClick={() => {
																		handlePreviousSuggestionClick(item, index);
																		clearPreviousAddressErrors(index);
																	}}
																>
																	<div className="suggestion-text">{item.Text}</div>
																	{item.Description && (
																		<div className="suggestion-description">
																			{item.Description}
																		</div>
																	)}
																</div>
															))}
														</div>
													)}
													{addressInput.errors.postcode && (
														<div
															className="error-message"
															data-testid={`confirm-error-previous-address-postcode-${index}`}
														>
															<p>Please enter a postcode</p>
														</div>
													)}
													{addressInput.errors.selection && (
														<div
															className="error-message"
															data-testid={`confirm-error-previous-address-selection-${index}`}
														>
															<p>Please select an address</p>
														</div>
													)}
													{addressInput.errors.duplicate && (
														<div
															className="error-message"
															data-testid={`confirm-error-previous-address-duplicate-${index}`}
														>
															<p>
																This address has already been provided or matches your
																current address
															</p>
														</div>
													)}
												</div>
											</div>
										))}


										{previousAddressInputs.length === 0 && selectedAddress && (
											<div style={{ marginBottom: "12px" }}>
												<ButtonComponent
													className="btn-w next-button"
													label="+ Click to add a previous address"
													onClick={addPreviousAddress}
													style={{ padding: "10px 20px" }}
													testId="confirm-add-previous-address"
												/>
											</div>
										)}


									</section>
								)}
							</>
						)}
					</div>
					{showStoredDetails && (
						<>
							{(compensate.signature || compensate.signatureToken) && (
								<div
									className="terms-section"
									data-testid="confirm-section-terms"
								>
									<p className="terms-intro">
										By pressing the "Submit" button below, I confirm that I have read
										and agree to the important information below:
									</p>
									<div className="terms-scroll">
										<p>
											I was not fully informed regarding the commission paid by
											{brand.id === BRAND_IDS.MLM
												? " my lender or lender group to the dealer who sold my car"
												: " the lender to the dealer when I purchased my vehicle"}
											.
										</p>
										{brand.id === BRAND_IDS.MLM ? (
											<>
												<p>
													I consent for My Law Matters to act on my behalf to make a
													motor finance claim(s) to my lender(s) on a no-win, no-fee
													basis. I understand that, if My Law Matters are successful
													in securing compensation for me, I will be charged a success
													fee of between 15% to 30% plus VAT (18% to 36% in total) of
													the compensation received.
												</p>
												<p>
													I have read and agree to the {mlmTerms()}, {privacyPolicy()}
													, {keyFacts("confirm-link-key-facts-terms")} and {representationConsentStatement()}. I
													agree for my digital signature held on file to be applied to
													these documents, copies of which will be e-mailed to me.
												</p>
												<p>
													I authorise My Law Matters to conduct identity verification
													and soft credit searches on my behalf through Valid8 IP Ltd,
													with no impact on my credit score. I also agree to{" "}
													{valid8PrivacyPolicy()}.
												</p>
												<p>
													I understand I do not have to use a law firm, and I can make
													a claim myself directly to my lender or the Financial
													Ombudsman Service at no cost.
												</p>
											</>
										) : (
											<>
												<p>
													I have read and agree to{" "}
													{termsAndConditions(
														`${brand.displayName} Terms of Engagement & Authority`,
													)}
													, {privacyPolicy()} and {keyFacts("confirm-link-key-facts-terms")}.
												</p>
												<p>
													I consent to {brand.displayName} referring my claim(s) to My
													Law Matters (MLM), who will act on a no-win no-fee basis,
													and if successful will charge {mlmFees()}. I have read and
													agree to the {mlmTerms()}, {mlmPrivacyPolicy()} and{" "}
													{representationConsentStatement()}. I agree for my digital
													signature held on file to be applied to these documents and
													e-mailed to me. I authorise MLM to conduct identity
													verification and soft credit searches on my behalf through
													Valid8 IP Ltd (Valid8), with no impact on my credit score
													and agree to {valid8PrivacyPolicy()}. I understand I can
													make a claim myself directly to my lender or the Financial
													Ombudsman Service at no cost.
												</p>
											</>
										)}
									</div>
								</div>
							)}


							<div
								className={`confirm-details-ready-panel${hideReadyCopy ? " email-source" : ""}`}
								data-testid="confirm-section-action-required"
							>
								{!hideReadyCopy && (
									<>
										<div className="confirm-details-ready-header">
											<h2><Target aria-hidden="true" size={18} />Action Required</h2>
											<FormActionButton
												nextTestId="confirm-btn-confirm"
												onNext={handleConfirm}
												nextTitle={submitting ? "Submitting..." : "Click To Submit"}
												greyedOut={submitting}
											/>
										</div>
										<div className="confirm-details-ready-copy">
											<ul>
												<li>
													<Check aria-hidden="true" size={14} />
													<span>I understand that I am an existing YCM customer and am applying to check if I have a mis-sold car finance claim.</span>
												</li>
												<li>
													<Check aria-hidden="true" size={14} />
													<span>Once submitted, we'll carry out your free eligibility check and let you know if you have any potential claims.</span>
												</li>
												<li>
													<Check aria-hidden="true" size={14} />
													<span>If eligible, your claim will be submitted to lenders on a No win, No Fee* basis. Only successful claims will be subject to a fee.</span>
												</li>
												<li>
													<Check aria-hidden="true" size={14} />
													<span>If we need any further information, we'll be in touch.</span>
												</li>
											</ul>
										</div>
									</>
								)}
								{hideReadyCopy && (
									<FormActionButton
										nextTestId="confirm-btn-confirm"
										onNext={handleConfirm}
										nextTitle={submitting ? "Submitting..." : "Click To Submit"}
										greyedOut={submitting}
									/>
								)}
							</div>


							{signatureError && (
								<div
									className="error-message"
									data-testid="confirm-error-signature"
								>
									<p>We could not find your signed authority. Please try again.</p>
								</div>
							)}
						</>
					)}

				</section>

				{showStoredDetails && (
					<section
						className="confirm-details-regulatory"
						aria-label="Regulatory information"
						data-testid="confirm-section-regulatory"
					>
						<p>
							*Your claim eligibility check is free. We will refer your claim to our
							partner law firm, My Law Matters (MLM), who will submit eligible
							claims to your lender(s) on a no win no fee basis, we will notify you
							before doing this. If successful, MLM charges 15%–30% + VAT (18%–36%
							total) of the amount recovered. A cancellation fee may apply after the
							14-day cooling-off period, as explained in MLM's documentation. MLM
							pays us a referral fee, which is not payable by you. You do not need
							to use a claims management company or law firm and can complain
							directly to your lender or the Financial Ombudsman Service for free.
							The FCA have estimated that individual claims will be worth an average of &pound;829. For further information and timelines visit:{" "}
							<span className="confirm-details-unlinked-reference">FCA news</span>{" "}
							and{" "}
							<span className="confirm-details-unlinked-reference">FCA policy</span>{" "}
							(page 12). Please see our {keyFacts("confirm-link-key-facts-regulatory")} for full details.
						</p>
						<hr className="confirm-details-regulatory-divider" />
						<p className="confirm-details-office-address">
							Your Claim Matters head office address: 84 Salop Street, Wolverhampton,
							WV3 0SR, United Kingdom.
						</p>
						<p>
							Your Claim Matters is a claims management company and is a trading style
							of Victor Hydon Ltd, Swinford House, Albion St, Brierley Hill DY5 3EE and
							is registered in England and Wales (Company No 09086740) VAT registration
							number 204 1226 68. Victor Hydon Ltd is regulated by the Financial Conduct
							Authority (Firm reference number: 833758). These details can be confirmed
							by visiting the Financial Services Register,{" "}
							<a href="https://www.fca.org.uk/register" target="_blank" rel="noopener noreferrer">
								www.fca.org.uk/register
							</a>
							.
						</p>
					</section>
				)}
			</div >
		</div >
	);
}
